<script lang="ts">
	// @ts-nocheck
	import slugify from 'slugify';
	import * as Drawer from '$lib/components/ui/drawer';
	import * as Tabs from '$lib/components/ui/tabs';
	import {
		CircleHelp,
		GraduationCap,
		House,
		Menu,
		Newspaper,
		Phone,
		Puzzle,
		Telescope,
		Ticket
	} from 'lucide-svelte';
	import Button from '../ui/button/button.svelte';
	import allToursData from '$lib/data/allTours.json';
	import { enhancedImages } from '$lib/utils/images';

	// Product code arrays for each tour type - these define the desired order
	const dayTourCodes = ['P18ZC5', 'PJHLE0', 'PNVP8M', 'PP0SGV', 'PTC0KJ', 'PNNK81'];
	const multiDayTourCodes = ['PTWH1T', 'P0V9R2', 'PVHGUQ', 'PTNCNK', 'PHKZNS', 'PU9VRB'];
	const shoreExcursionCodes = ['P0J8QR', 'PM0VRV', 'PB2LG2', 'PSP9WG', 'P9TVKQ', 'PKLVUQ'];

	// Create a map for quick lookup of tours by product code
	let toursByCode = $derived(
		allToursData.reduce((map, tour) => {
			map[tour.productCode] = tour;
			return map;
		}, {})
	);

	// Build the tour arrays in the specified order
	let dayTours = $derived(
		dayTourCodes
			.map((code) => toursByCode[code]) // Get the tour for each code
			.filter((tour) => tour !== undefined) // Filter out any codes not found in allToursData
	);

	let multiDayTours = $derived(
		multiDayTourCodes.map((code) => toursByCode[code]).filter((tour) => tour !== undefined)
	);

	let shoreExcursions = $derived(
		shoreExcursionCodes.map((code) => toursByCode[code]).filter((tour) => tour !== undefined)
	);

	// Function to check if an image URL is in the enhancedImages collection
	function isEnhancedImage(imageUrl) {
		if (!imageUrl) return false;
		return !!Object.entries(enhancedImages).find(([key]) => key === imageUrl);
	}

	// Function to get the appropriate image source
	function getImageSource(imageUrl) {
		if (!imageUrl) return null;

		// If it's an enhanced image, get it from the enhancedImages collection
		const enhancedEntry = Object.entries(enhancedImages).find(([key]) => key === imageUrl);
		if (enhancedEntry) {
			return enhancedEntry[1].default;
		}

		// Otherwise, use the URL directly
		return imageUrl;
	}
	// This variable is not currently used in the component but kept for future reference
	// let privateTours = $derived(allToursData.filter(tour => tour.productType === 'PRIVATETOUR' && privateTourCodes.includes(tour.productCode)));

	let showAllDayTours = $state(false);
	let showAllMultiDayTours = $state(false);
	let showAllShoreExcursions = $state(false);
	// This variable is not currently used in the component but kept for future reference
	// let showAllPrivateTours = false;

	let isOpen = $state(false);
</script>

<div class="xl:hidden">
	<Drawer.Root bind:open={isOpen}>
		<Drawer.Trigger>
			<Button class="h-8 w-8 rounded-full px-0" variant="ghost" size="icon">
				<Menu class="h-4 w-4" />
				<span class="sr-only">Menu</span>
			</Button>
		</Drawer.Trigger>
		<Drawer.Content class="flex h-[80vh] flex-col">
			<Tabs.Root value="tours" class=" h-full w-full">
				<Tabs.List class="grid grid-cols-2 bg-background pb-12 pt-6">
					<Tabs.Trigger
						class="rounded-none focus-visible:ring-0 data-[state=active]:border-b-2 data-[state=active]:border-tt-red data-[state=active]:bg-background data-[state=active]:font-semibold data-[state=active]:text-tt-red data-[state=active]:shadow-none"
						value="tours">Tours</Tabs.Trigger
					>
					<Tabs.Trigger
						class="rounded-none focus-visible:ring-0 data-[state=active]:border-b-2 data-[state=active]:border-tt-red data-[state=active]:bg-background data-[state=active]:font-semibold data-[state=active]:text-tt-red data-[state=active]:shadow-none"
						value="menu">Menu</Tabs.Trigger
					>
				</Tabs.List>
				<Tabs.Content id="mobileNav" value="tours" class="h-[65vh] overflow-auto px-4"
					><div class="flex flex-col pb-6">
						<div class="flex items-center justify-between pb-4">
							<h2
								class="scroll-m-20 text-xl font-semibold tracking-tight transition-colors first:mt-0"
							>
								Day Tours
							</h2>
							<Button onclick={() => (isOpen = false)} href="/tours#dayTours" variant="link"
								>{showAllDayTours ? 'Show Less' : 'Show All'}</Button
							>
						</div>

						<div class="grid grid-cols-2 gap-4">
							{#each showAllDayTours ? dayTours : dayTours.slice(0, 4) as tour}
								<a
									onclick={() => (isOpen = false)}
									data-sveltekit-noscroll
									href={`/tours/${slugify(tour.name, { lower: true, strict: true, remove: `/[*+~.()'"!:@]/g` })}`}
									data-sveltekit-prefetch
									class="group relative flex aspect-video w-full items-center justify-center overflow-hidden rounded-xl bg-gray-900 px-4 py-20 shadow-xl"
								>
									{#if isEnhancedImage(tour.images[0].itemUrl)}
										<enhanced:img
											class="absolute inset-0 h-full w-full object-cover"
											src={getImageSource(tour.images[0].itemUrl)}
											alt={tour.name}
											sizes="(min-width: 640px) 50vw, 100vw"
											loading="lazy"
										/>
									{:else}
										<img
											class="absolute inset-0 h-full w-full object-cover"
											src={tour.images[0].itemUrl}
											alt={tour.name}
											sizes="(min-width: 640px) 50vw, 100vw"
											loading="lazy"
										/>
									{/if}
									<div
										class="w-full-hover:bg-foreground/50 group absolute inset-0 bg-foreground/40 mix-blend-multiply transition-all"
									></div>

									<div class="relative mx-auto lg:mx-0">
										<h3 class="text-md text-center font-bold text-background">{tour.name}</h3>
									</div>
								</a>
							{/each}
						</div>
					</div>
					<div class="flex flex-col pb-6">
						<div class="flex items-center justify-between pb-4">
							<h2
								class="scroll-m-20 text-xl font-semibold tracking-tight transition-colors first:mt-0"
							>
								Multi-day Tours
							</h2>
							<Button href="/tours#multiDayTours" onclick={() => (isOpen = false)} variant="link"
								>{showAllMultiDayTours ? 'Show Less' : 'Show All'}</Button
							>
						</div>

						<div class="grid grid-cols-2 gap-4">
							{#each showAllMultiDayTours ? multiDayTours : multiDayTours.slice(0, 4) as tour}
								<a
									onclick={() => (isOpen = false)}
									data-sveltekit-noscroll
									href={`/tours/${slugify(tour.name, { lower: true, strict: true, remove: `/[*+~.()'"!:@]/g` })}`}
									data-sveltekit-prefetch
									class="group relative flex aspect-video w-full items-center justify-center overflow-hidden rounded-xl bg-gray-900 px-4 py-20 shadow-xl"
								>
									{#if isEnhancedImage(tour.images[0].itemUrl)}
										<enhanced:img
											class="absolute inset-0 h-full w-full object-cover"
											src={getImageSource(tour.images[0].itemUrl)}
											alt={tour.name}
											sizes="(min-width: 640px) 50vw, 100vw"
											loading="lazy"
										/>
									{:else}
										<img
											class="absolute inset-0 h-full w-full object-cover"
											src={tour.images[0].itemUrl}
											alt={tour.name}
											sizes="(min-width: 640px) 50vw, 100vw"
											loading="lazy"
										/>
									{/if}
									<div
										class="w-full-hover:bg-foreground/50 group absolute inset-0 bg-foreground/40 mix-blend-multiply transition-all"
									></div>

									<div class="relative mx-auto lg:mx-0">
										<h3 class="text-center text-sm font-bold text-background">{tour.name}</h3>
									</div>
								</a>
							{/each}
						</div>
					</div>
					<div class="flex flex-col pb-6">
						<div class="flex items-center justify-between pb-4">
							<h2
								class="scroll-m-20 text-xl font-semibold tracking-tight transition-colors first:mt-0"
							>
								Shore Excursions
							</h2>
							<Button onclick={() => (isOpen = false)} href="/tours#shoreExcursions" variant="link"
								>{showAllShoreExcursions ? 'Show Less' : 'Show All'}</Button
							>
						</div>

						<div class="grid grid-cols-2 gap-4">
							{#each showAllShoreExcursions ? shoreExcursions : shoreExcursions.slice(0, 4) as tour}
								<a
									onclick={() => (isOpen = false)}
									data-sveltekit-noscroll
									href={`/tours/${slugify(tour.name, { lower: true, strict: true, remove: `/[*+~.()'"!:@]/g` })}`}
									data-sveltekit-prefetch
									class="group relative flex aspect-video w-full items-center justify-center overflow-hidden rounded-xl bg-gray-900 px-4 py-20 shadow-xl"
								>
									{#if isEnhancedImage(tour.images[0].itemUrl)}
										<enhanced:img
											class="absolute inset-0 h-full w-full object-cover"
											src={getImageSource(tour.images[0].itemUrl)}
											alt={tour.name}
											sizes="(min-width: 640px) 50vw, 100vw"
											loading="lazy"
										/>
									{:else}
										<img
											class="absolute inset-0 h-full w-full object-cover"
											src={tour.images[0].itemUrl}
											alt={tour.name}
											sizes="(min-width: 640px) 50vw, 100vw"
											loading="lazy"
										/>
									{/if}
									<div
										class="w-full-hover:bg-foreground/50 group absolute inset-0 bg-foreground/40 mix-blend-multiply transition-all"
									></div>

									<div class="relative mx-auto lg:mx-0">
										<h3 class="text-md text-center font-bold text-background">{tour.name}</h3>
									</div>
								</a>
							{/each}
						</div>
					</div>
					<div class="flex flex-col pb-6">
						<div class="flex items-center justify-between pb-4">
							<h2
								class="scroll-m-20 text-xl font-semibold tracking-tight transition-colors first:mt-0"
							>
								Private Tours
							</h2>

							<Button
								onclick={() => (isOpen = false)}
								href="/private-tours"
								data-sveltekit-noscroll
								variant="link">Show All</Button
							>
						</div>

						<div class="grid grid-cols-2 gap-4">
							<div class="col-span-2">
								<a
									onclick={() => (isOpen = false)}
									data-sveltekit-noscroll
									href="/private-tours"
									data-sveltekit-prefetch
									class="group relative flex aspect-video h-[160px] w-full items-center justify-center overflow-hidden rounded-xl bg-gray-900 px-4 py-20 shadow-xl"
								>
									<enhanced:img
										loading="eager"
										class="absolute inset-0 h-full w-full object-cover"
										src="$lib/assets/images/private-tours-1.jpg"
										alt="Private tours"
									/>
									<div
										class="w-full-hover:bg-foreground/50 group absolute inset-0 bg-foreground/40 mix-blend-multiply transition-all"
									></div>

									<div class="relative mx-auto lg:mx-0">
										<h3 class="text-md text-center font-bold text-background">
											Private Multi-day Tours
										</h3>
									</div>
								</a>
							</div>
							<div class="col-span-2">
								<a
									onclick={() => (isOpen = false)}
									data-sveltekit-noscroll
									href="/custom-private-tours"
									data-sveltekit-prefetch
									class="group relative flex aspect-video h-[160px] w-full items-center justify-center overflow-hidden rounded-xl bg-gray-900 px-4 py-20 shadow-xl"
								>
									<enhanced:img
										loading="eager"
										class="absolute inset-0 h-full w-full object-cover"
										src="$lib/assets/images/wineglass.jpg"
										alt="Custom Private tours"
									/>
									<div
										class="w-full-hover:bg-foreground/50 group absolute inset-0 bg-foreground/40 mix-blend-multiply transition-all"
									></div>

									<div class="relative mx-auto lg:mx-0">
										<h3 class="text-md text-center font-bold text-background">
											Custom Private Tours
										</h3>
									</div>
								</a>
							</div>

							<!-- {#each showAllPrivateTours ? privateTours : privateTours.slice(0, 4) as tour}
								<Drawer.Close>
									<a
										data-sveltekit-noscroll
										href={`/tours/${slugify(tour.name, { lower: true, strict: true, remove: `/[*+~.()'"!:@]/g` })}`}
										data-sveltekit-prefetch
										class="group relative flex aspect-video w-full items-center justify-center overflow-hidden rounded-xl bg-gray-900 px-4 py-20 shadow-xl"
									>
										<enhanced:img
											class="absolute inset-0 h-full w-full object-cover"
											src={Object.entries(enhancedImages).find(
												([key, value]) => key === tour.images[0].itemUrl
											)[1].default}
											sizes="(min-width: 640px) 50vw, 100vw"
											loading="lazy"
											alt={tour.name}
										/>
										<div
											class="w-full-hover:bg-foreground/50 group absolute inset-0 bg-foreground/40 mix-blend-multiply transition-all"
										></div>

										<div class="relative mx-auto lg:mx-0">
											<h3 class="text-md text-center font-bold text-background">
												{tour.name.split('-')[1]}
											</h3>
										</div>
									</a>
								</Drawer.Close>
							{/each} -->
						</div>
					</div>

					<Drawer.Close class="w-full">
						<Button
							href="/tours#dayTours"
							class="mb-12 w-full rounded-full bg-tt-red font-semibold hover:bg-tt-red/80"
							>View all tours</Button
						></Drawer.Close
					>
				</Tabs.Content>
				<Tabs.Content value="menu">
					<div class="flex flex-col">
						<Button
							onclick={() => (isOpen = false)}
							data-sveltekit-noscroll
							href="/"
							variant="ghost"
							class="col-span-4 flex  justify-start rounded-none border-b py-6"
							><House class="mr-2 h-4 w-4" /> Home</Button
						>

						<Button
							onclick={() => (isOpen = false)}
							data-sveltekit-noscroll
							href="/tours#dayTours"
							variant="ghost"
							class="col-span-4 flex  justify-start rounded-none border-b py-6"
							><Ticket class="mr-2 h-4 w-4" /> All Tours</Button
						>

						<Button
							onclick={() => (isOpen = false)}
							data-sveltekit-noscroll
							href="/private-tours"
							variant="ghost"
							class="col-span-4 flex  justify-start rounded-none border-b py-6"
							><Telescope class="mr-2 h-4 w-4" /> Private Tours</Button
						>

						<Button
							onclick={() => (isOpen = false)}
							data-sveltekit-noscroll
							href="/custom-private-tours"
							variant="ghost"
							class="col-span-4 flex  justify-start rounded-none border-b py-6"
							><Puzzle class="mr-2 h-4 w-4" /> Custom Private Tours</Button
						>

						<Button
							onclick={() => (isOpen = false)}
							data-sveltekit-noscroll
							href="/blog"
							variant="ghost"
							class="col-span-4 flex  justify-start rounded-none border-b py-6"
							><Newspaper class="mr-2 h-4 w-4" /> Blog</Button
						>

						<Button
							onclick={() => (isOpen = false)}
							data-sveltekit-noscroll
							href="/tours#dayTours"
							variant="ghost"
							class="col-span-4 flex  justify-start rounded-none border-b py-6"
							><CircleHelp class="mr-2 h-4 w-4" /> Adventure Tasmania</Button
						>
						<Button
							onclick={() => (isOpen = false)}
							data-sveltekit-noscroll
							href="/tour-guide-academy"
							variant="ghost"
							class="col-span-4 flex  justify-start rounded-none border-b py-6"
							><GraduationCap class="mr-2 h-4 w-4" /> Tour Guide Academy</Button
						>

						<Button
							onclick={() => (isOpen = true)}
							data-sveltekit-noscroll
							href="/contact"
							variant="ghost"
							class="col-span-4 flex  justify-start rounded-none border-b py-6"
							><Phone class="mr-2 h-4 w-4" /> Contact</Button
						>
					</div>
				</Tabs.Content>
			</Tabs.Root>
		</Drawer.Content>
	</Drawer.Root>
</div>
